import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { AlertTriangle, Eye, Plus } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { AdvanceAmountsCell } from "@/components/requests/AdvanceAmountsCell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/Combobox";
import { DatePicker } from "@/components/DatePicker";
import {
  OtherAdvanceAmountsField,
  otherAdvanceAmountsError
} from "@/components/requests/OtherAdvanceAmountsField";
import { SettlementAmountCell } from "@/components/requests/SettlementAmountCell";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DataTable } from "@/components/data-table/DataTable";
import { SortableHeader } from "@/components/data-table/SortableHeader";
import { type AppTableFeatures } from "@/components/data-table/features";
import SegmentedTabs from "@/components/SegmentedTabs";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { RefreshButton } from "@/components/RefreshButton";
import { SummaryRow } from "@/components/SummaryRow";
import { AdvanceDetailDialog } from "@/components/requests/AdvanceDetailDialog";
import { FinanceNoticeDialog } from "@/components/requests/FinanceNoticeDialog";
import { DownloadReportButton } from "@/components/requests/DownloadReportButton";
import { RequestDetailDialog } from "@/components/requests/RequestDetailDialog";
import { billTotals } from "@/lib/bills";
import {
  ApiError,
  createOtherAdvance,
  discardMyRequest,
  fetchAdvance,
  FinanceApprover,
  fetchFinanceApprovers,
  fetchMyOtherAdvances,
  fetchMyOtherRequests,
  fetchProfile,
  resubmitMyAdvance
} from "../api";
import {
  Advance,
  AdvanceAmount,
  AdvanceStatus,
  OTHER_CLAIM_TYPE_LABEL,
  OTHER_CLAIM_TYPES,
  OtherClaimType,
  TRAVEL_REQUEST_STATUS_LABEL,
  TravelRequestStatus,
  TravelRequestSummary
} from "../types";
import { formatDate } from "@/lib/datetime";

// The Other reimbursements flow (City travel / Miscellaneous) - same shape
// as My Reimbursements (MyRequests.tsx): a requests tab and an advances tab,
// status filters, the draft reminder, and the shared detail sheets. Kept a
// separate page rather than a mode of MyRequests so the travel page is
// untouched.

// Same "YYYY-MM-DD" shape DatePicker's value expects - see its own
// toDateOnlyString, not exported from there.
function toDateOnlyString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

type View = "requests" | "advances";
type StatusFilter = "All" | TravelRequestStatus;
type AdvanceStatusFilter = "All" | AdvanceStatus;

const EMPTY_COPY: Record<StatusFilter, string> = {
  All: "No City travel or Miscellaneous claims yet. Create one to get started.",
  Draft: "No draft claims.",
  Submitted: "No submitted claims awaiting a decision.",
  PendingFinance: "No claims awaiting Finance Approver sign-off.",
  Approved: "No approved claims yet.",
  Rejected: "No rejected claims.",
  Completed: "No settled claims yet.",
  ResubmitRequested: "Nothing needs changes right now."
};

const ADVANCE_STATUS_LABEL: Record<AdvanceStatus, string> = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
  Paid: "Paid",
  Resubmit: "Needs changes"
};

const ADVANCE_EMPTY_COPY: Record<AdvanceStatusFilter, string> = {
  All: "No advances for City travel or Miscellaneous yet. Raise one to get started.",
  Pending: "No pending advances.",
  Approved: "No approved-but-unpaid advances.",
  Rejected: "No rejected advances.",
  Paid: "No paid advances yet.",
  Resubmit: "Nothing needs changes right now."
};

const VALID_STATUS_PARAMS = new Set<StatusFilter>([
  "All", "Draft", "Submitted", "PendingFinance", "Approved", "Rejected", "Completed", "ResubmitRequested"
]);
const VALID_ADVANCE_STATUS_PARAMS = new Set<AdvanceStatusFilter>([
  "All", "Pending", "Approved", "Rejected", "Paid", "Resubmit"
]);

const columnHelper = createColumnHelper<AppTableFeatures, TravelRequestSummary>();
const advanceColumnHelper = createColumnHelper<AppTableFeatures, Advance>();

const claimTypeLabel = (t?: OtherClaimType | null) => (t ? OTHER_CLAIM_TYPE_LABEL[t] : "-");

export default function OtherReimbursements() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialView: View =
    searchParams.get("view") === "advances" || searchParams.get("advance") ? "advances" : "requests";
  const statusParam = searchParams.get("status") as StatusFilter;
  const advanceStatusParam = searchParams.get("advanceStatus") as AdvanceStatusFilter;

  const [view, setView] = React.useState<View>(initialView);
  const [rows, setRows] = React.useState<TravelRequestSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState<StatusFilter>(
    VALID_STATUS_PARAMS.has(statusParam) ? statusParam : "All"
  );
  const [advanceRows, setAdvanceRows] = React.useState<Advance[]>([]);
  const [advancesLoading, setAdvancesLoading] = React.useState(true);
  const [advanceStatus, setAdvanceStatus] = React.useState<AdvanceStatusFilter>(
    VALID_ADVANCE_STATUS_PARAMS.has(advanceStatusParam) ? advanceStatusParam : "All"
  );

  const [viewRequestId, setViewRequestId] = React.useState<string | null>(() => searchParams.get("request"));
  const [viewAdvanceId, setViewAdvanceId] = React.useState<string | null>(() => searchParams.get("advance"));
  const [highlightOnOpen, setHighlightOnOpen] = React.useState(
    () => !!searchParams.get("request") || !!searchParams.get("advance")
  );

  const load = React.useCallback(() => {
    setLoading(true);
    fetchMyOtherRequests()
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);
  const loadAdvances = React.useCallback(() => {
    setAdvancesLoading(true);
    fetchMyOtherAdvances()
      .then(setAdvanceRows)
      .finally(() => setAdvancesLoading(false));
  }, []);
  React.useEffect(() => {
    load();
    loadAdvances();
  }, [load, loadAdvances]);

  const handleStaleDecision = (err: unknown, reload: () => void) => {
    if (err instanceof ApiError && err.status === 409) {
      toast.error(
        err.message ||
          "This has already changed (maybe from another tab, or an approver acted on it) - the list has been refreshed to show its current status."
      );
      reload();
      return true;
    }
    return false;
  };

  // --- Requests -----------------------------------------------------------
  const draftRows = React.useMemo(
    () => rows.filter((r) => r.status === "Draft").sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [rows]
  );
  const [showDraftReminder, setShowDraftReminder] = React.useState(false);
  const handleNewRequestClick = () => {
    if (draftRows.length > 0) setShowDraftReminder(true);
    else navigate("/other-reimbursements/new");
  };

  const [discardTarget, setDiscardTarget] = React.useState<TravelRequestSummary | null>(null);
  const [discarding, setDiscarding] = React.useState(false);
  const confirmDiscard = async () => {
    if (!discardTarget) return;
    setDiscarding(true);
    try {
      await discardMyRequest(discardTarget.id);
      setDiscardTarget(null);
      load();
      loadAdvances(); // a linked advance is released
      toast.success("Draft discarded");
    } catch (err) {
      if (!handleStaleDecision(err, load)) {
        toast.error(err instanceof ApiError ? err.message : "Could not discard this draft - check your connection and try again.");
      } else {
        setDiscardTarget(null);
      }
    } finally {
      setDiscarding(false);
    }
  };

  const data = React.useMemo(
    () => (status === "All" ? rows : rows.filter((r) => r.status === status)),
    [rows, status]
  );
  const advanceData = React.useMemo(
    () => (advanceStatus === "All" ? advanceRows : advanceRows.filter((r) => r.status === advanceStatus)),
    [advanceRows, advanceStatus]
  );

  // --- Advance sheet ------------------------------------------------------
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingAdvanceId, setEditingAdvanceId] = React.useState<string | null>(null);
  const [editingAdvanceLoading, setEditingAdvanceLoading] = React.useState(false);
  const [advResubmitNote, setAdvResubmitNote] = React.useState<{ stage: "Manager" | "Finance"; comment: string } | null>(null);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [advClaimType, setAdvClaimType] = React.useState<OtherClaimType | "">("");
  const [advPurpose, setAdvPurpose] = React.useState("");
  const [advAmounts, setAdvAmounts] = React.useState<AdvanceAmount[]>([{ currency: "", amount: 0 }]);
  // A record only - the planned date of the trip/errand this advance is
  // for (today or later). Never fed into FX or matching (the ledger keeps
  // using createdAt). Defaults to today.
  const [advDate, setAdvDate] = React.useState(() => toDateOnlyString(new Date()));
  const [advFinanceApproverId, setAdvFinanceApproverId] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const resetAdvanceForm = () => {
    setAdvClaimType("");
    setAdvPurpose("");
    setAdvAmounts([{ currency: "", amount: 0 }]);
    setAdvDate(toDateOnlyString(new Date()));
    setAdvFinanceApproverId("");
    setReviewOpen(false);
    setEditingAdvanceId(null);
    setAdvResubmitNote(null);
  };

  React.useEffect(() => {
    if (!editingAdvanceId) return;
    setEditingAdvanceLoading(true);
    setError(null);
    fetchAdvance(editingAdvanceId)
      .then((a) => {
        setAdvClaimType(a.claimType || "");
        setAdvPurpose(a.flightDetails || "");
        setAdvAmounts(a.amounts.length > 0 ? a.amounts : [{ currency: "", amount: 0 }]);
        setAdvDate(a.advanceDate || toDateOnlyString(new Date()));
        setAdvResubmitNote(
          a.resubmitComment
            ? { stage: a.resubmitRequestedBy === "Finance" ? "Finance" : "Manager", comment: a.resubmitComment }
            : null
        );
        setDialogOpen(true);
      })
      .catch(() => {
        setError("Could not load this advance for editing.");
        setEditingAdvanceId(null);
      })
      .finally(() => setEditingAdvanceLoading(false));
  }, [editingAdvanceId]);

  const [financeApprovers, setFinanceApprovers] = React.useState<FinanceApprover[]>([]);
  const [financeApproversLoading, setFinanceApproversLoading] = React.useState(true);
  React.useEffect(() => {
    fetchFinanceApprovers()
      .then(setFinanceApprovers)
      .catch(() => setFinanceApprovers([]))
      .finally(() => setFinanceApproversLoading(false));
  }, []);
  const [designation, setDesignation] = React.useState("");
  const [hasReportingManager, setHasReportingManager] = React.useState(true);
  React.useEffect(() => {
    fetchProfile()
      .then((profile) => {
        if (profile.jobTitle) setDesignation(profile.jobTitle);
        setHasReportingManager(!!profile.hasReportingManager);
      })
      .catch(() => {});
  }, []);

  const validateAdvance = (): string | null => {
    if (!advClaimType) return "Select City travel or Miscellaneous.";
    if (!advPurpose.trim()) return "Enter the purpose of this advance.";
    const amountsError = otherAdvanceAmountsError(advAmounts);
    if (amountsError) return amountsError;
    if (!editingAdvanceId && !financeApproversLoading && financeApprovers.length > 0 && !advFinanceApproverId) {
      return "Select a Finance Approver.";
    }
    return null;
  };

  const [financeNoticeOpen, setFinanceNoticeOpen] = React.useState(false);
  const [financeCc, setFinanceCc] = React.useState<string[]>([]);

  const submitAdvance = async (ccEmails?: string[]) => {
    if (!advClaimType) return;
    setSaving(true);
    setError(null);
    try {
      const result = await createOtherAdvance({
        claimType: advClaimType,
        purpose: advPurpose.trim(),
        amounts: advAmounts,
        advanceDate: advDate || undefined,
        financeApproverId: advFinanceApproverId || undefined,
        ccEmails
      });
      setDialogOpen(false);
      setFinanceNoticeOpen(false);
      resetAdvanceForm();
      loadAdvances();
      toast.success(
        result.skippedToFinance
          ? "Submitted - you have no reporting manager on file, so this went straight to your Finance Approver."
          : "Advance request submitted"
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit this advance request - check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  // Resubmits through the shared advance route - its validation branches on
  // the advance being an Other one (purpose + uncapped amounts only).
  const submitResubmitAdvance = async () => {
    if (!editingAdvanceId) return;
    setSaving(true);
    setError(null);
    try {
      await resubmitMyAdvance(editingAdvanceId, {
        amounts: advAmounts,
        flightDetails: advPurpose.trim(),
        advanceDate: advDate || null
      });
      setDialogOpen(false);
      resetAdvanceForm();
      loadAdvances();
      toast.success("Advance resubmitted");
    } catch (err) {
      if (!handleStaleDecision(err, loadAdvances)) {
        setError(err instanceof ApiError ? err.message : "Could not resubmit this advance - check your connection and try again.");
      } else {
        setDialogOpen(false);
        resetAdvanceForm();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitAdvanceClick = () => {
    const problem = validateAdvance();
    if (problem) {
      toast.error(problem);
      return;
    }
    setError(null);
    setReviewOpen(true);
  };

  const confirmSubmitAdvance = () => {
    if (editingAdvanceId) {
      void submitResubmitAdvance();
      return;
    }
    if (!hasReportingManager) {
      setReviewOpen(false);
      setDialogOpen(false);
      setFinanceCc([]);
      setFinanceNoticeOpen(true);
      return;
    }
    void submitAdvance();
  };

  // --- Columns ------------------------------------------------------------
  const columns = React.useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: ({ column }) => <SortableHeader column={column}>Purpose</SortableHeader>,
          cell: ({ row }) => (
            <span className="line-clamp-2 max-w-[18rem] text-sm font-medium">{row.original.flightDetails || "-"}</span>
          )
        }),
        columnHelper.accessor((row) => claimTypeLabel(row.claimType), {
          id: "claimType",
          meta: { label: "Claim type" },
          header: ({ column }) => <SortableHeader column={column}>Claim type</SortableHeader>,
          cell: ({ row }) => <span className="text-sm">{claimTypeLabel(row.original.claimType)}</span>
        }),
        columnHelper.accessor("country", {
          id: "country",
          meta: { label: "Country" },
          header: ({ column }) => <SortableHeader column={column}>Country</SortableHeader>,
          cell: ({ row }) => <span className="text-sm">{row.original.country || "-"}</span>
        }),
        columnHelper.accessor(
          (row) => billTotals(row.expenses, row.currency, 0, null).totalInRequestCurrency,
          {
            id: "amount",
            meta: { label: "Amount" },
            header: ({ column }) => <SortableHeader column={column}>Amount</SortableHeader>,
            cell: ({ row }) => {
              const totals = billTotals(row.original.expenses, row.original.currency, 0, null);
              const lines = [
                ...(totals.totalInRequestCurrency > 0
                  ? [`${row.original.currency} ${totals.totalInRequestCurrency.toLocaleString()}`]
                  : []),
                ...totals.others.map((o) => `${o.currency} ${o.total.toLocaleString()}`)
              ];
              return lines.length > 0 ? (
                <div className="whitespace-nowrap text-sm">
                  {lines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              ) : (
                <span className="text-sm">-</span>
              );
            }
          }
        ),
        // Same "Net payable to the employee" figure CurrencyConversionCard's
        // bottom tile shows in the Request Details dialog - see
        // MyRequests.tsx's identical column for the full reasoning. An
        // Other reimbursement has no trip/departure, so its advance (like
        // everywhere else this falls back) converts at the request's
        // created date instead.
        columnHelper.display({
          id: "settlementAmount",
          meta: { label: "Settlement amount" },
          header: "Settlement amount",
          cell: ({ row }) => (
            <SettlementAmountCell
              requestId={row.original.id}
              claimMethod={row.original.claimMethod}
              currency={row.original.currency}
              expenses={row.original.expenses}
              estimatedAllowanceAmount={row.original.estimatedAllowanceAmount}
              departureDate={row.original.departureAt || row.original.createdAt}
              linkedAdvanceId={row.original.linkedAdvanceId}
              settlementTargetByCurrency={row.original.settlementTargetByCurrency}
              fxOverrides={row.original.fxOverrides}
            />
          )
        }),
        columnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
          cell: ({ row }) => (
            <StatusBadge status={row.original.status} label={TRAVEL_REQUEST_STATUS_LABEL[row.original.status]} />
          )
        }),
        columnHelper.accessor("createdAt", {
          id: "created",
          meta: { label: "Created" },
          sortFn: "datetime",
          header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        columnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const r = row.original;
            if (r.status === "Draft") {
              return (
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() => navigate(`/other-reimbursements/new?draftId=${encodeURIComponent(r.id)}`)}
                  >
                    Continue
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setDiscardTarget(r)}>
                    Discard
                  </Button>
                </div>
              );
            }
            return (
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" className="h-8" onClick={() => setViewRequestId(r.id)}>
                  <Eye className="mr-1 h-3.5 w-3.5" /> View
                </Button>
                <DownloadReportButton travelRequestId={r.id} />
                {r.status === "ResubmitRequested" && (
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      navigate(`/other-reimbursements/new?draftId=${encodeURIComponent(r.id)}&resubmit=1`)
                    }
                  >
                    Edit & resubmit
                  </Button>
                )}
              </div>
            );
          }
        })
      ]),
    [navigate]
  );

  const advanceColumns = React.useMemo(
    () =>
      advanceColumnHelper.columns([
        advanceColumnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: ({ column }) => <SortableHeader column={column}>Purpose</SortableHeader>,
          cell: ({ row }) => (
            <span className="line-clamp-2 max-w-[18rem] text-sm font-medium">{row.original.flightDetails || "-"}</span>
          )
        }),
        advanceColumnHelper.accessor((row) => claimTypeLabel(row.claimType), {
          id: "claimType",
          meta: { label: "Claim type" },
          header: ({ column }) => <SortableHeader column={column}>Claim type</SortableHeader>,
          cell: ({ row }) => <span className="text-sm">{claimTypeLabel(row.original.claimType)}</span>
        }),
        advanceColumnHelper.accessor((row) => row.amounts[0]?.amount ?? 0, {
          id: "amount",
          meta: { label: "Amount" },
          header: ({ column }) => <SortableHeader column={column}>Amount</SortableHeader>,
          cell: ({ row }) => <AdvanceAmountsCell amounts={row.original.amounts} />
        }),
        advanceColumnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
          cell: ({ row }) => (
            <StatusBadge status={row.original.status} label={ADVANCE_STATUS_LABEL[row.original.status]} />
          )
        }),
        advanceColumnHelper.display({
          id: "linked",
          meta: { label: "Linked to" },
          header: "Linked to",
          cell: ({ row }) => {
            const summary = row.original.linkedRequestSummary;
            if (!row.original.linkedRequestId) return <span className="text-sm text-muted-foreground">-</span>;
            return (
              <div className="text-sm">
                <p className="font-medium">Claim</p>
                <p className="text-xs text-muted-foreground">
                  {summary?.status ? TRAVEL_REQUEST_STATUS_LABEL[summary.status as TravelRequestStatus] ?? summary.status : "-"}
                </p>
              </div>
            );
          }
        }),
        advanceColumnHelper.accessor("createdAt", {
          id: "created",
          meta: { label: "Requested" },
          sortFn: "datetime",
          header: ({ column }) => <SortableHeader column={column}>Requested</SortableHeader>,
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        advanceColumnHelper.display({
          id: "view",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const a = row.original;
            return (
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" className="h-8" onClick={() => setViewAdvanceId(a.id)}>
                  <Eye className="mr-1 h-3.5 w-3.5" /> View
                </Button>
                {a.status === "Resubmit" && (
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() => setEditingAdvanceId(a.id)}
                    disabled={editingAdvanceLoading}
                  >
                    Edit & resubmit
                  </Button>
                )}
              </div>
            );
          }
        })
      ]),
    [editingAdvanceLoading]
  );

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="Other Reimbursements"
        description="City travel and miscellaneous claims and advances you have raised, and where each one stands."
        titleActions={
          <RefreshButton
            onRefresh={view === "requests" ? load : loadAdvances}
            isLoading={view === "requests" ? loading : advancesLoading}
          />
        }
      />

      <Tabs value={view} onValueChange={(v) => setView(v as View)}>
        <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
          <TabsTrigger value="requests" className="min-w-0">
            {/* TabsTrigger (shadcn) forces whitespace-nowrap, which
               overflowed its grid-cols-2 cell on narrow phones and
               overlapped the Advances trigger next to it - shorter copy
               there instead of trying to wrap. */}
            <span className="hidden sm:inline">Reimbursement Requests</span>
            <span className="truncate sm:hidden">Requests</span>
          </TabsTrigger>
          <TabsTrigger value="advances" className="min-w-0">Advances</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "requests" ? (
        <DataTable
          columns={columns}
          data={data}
          isLoading={loading}
          filterColumn="purpose"
          filterPlaceholder="Search purpose…"
          emptyMessage={EMPTY_COPY[status]}
          persistKey="other-reimbursements:requests"
          toolbarStart={
            <SegmentedTabs
              value={status}
              onChange={setStatus}
              className="h-9 w-[7.25rem] sm:w-[11.25rem]"
              options={[
                { value: "All", label: "All" },
                { value: "Draft", label: "Draft" },
                { value: "Submitted", label: "Submitted" },
                { value: "PendingFinance", label: "Pending" },
                { value: "Approved", label: "Approved" },
                { value: "Rejected", label: "Rejected" },
                { value: "Completed", label: "Completed" },
                { value: "ResubmitRequested", label: "Needs changes" }
              ]}
            />
          }
          toolbarEnd={
            <Button size="sm" className="h-9" onClick={handleNewRequestClick}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New request
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={advanceColumns}
          data={advanceData}
          isLoading={advancesLoading}
          filterColumn="purpose"
          filterPlaceholder="Search purpose…"
          emptyMessage={ADVANCE_EMPTY_COPY[advanceStatus]}
          persistKey="other-reimbursements:advances"
          toolbarStart={
            <SegmentedTabs
              value={advanceStatus}
              onChange={setAdvanceStatus}
              className="h-9 w-[7.25rem] sm:w-[11.25rem]"
              options={[
                { value: "All", label: "All" },
                { value: "Pending", label: "Pending" },
                { value: "Approved", label: "Approved" },
                { value: "Paid", label: "Paid" },
                { value: "Rejected", label: "Rejected" },
                { value: "Resubmit", label: "Needs changes" }
              ]}
            />
          }
          toolbarEnd={
            <Button size="sm" className="h-9" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Request advance
            </Button>
          }
        />
      )}

      <Sheet
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setError(null);
            resetAdvanceForm();
          }
        }}
      >
        <SheetContent className="flex w-full flex-col sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{editingAdvanceId ? "Edit & resubmit advance" : "Request an advance"}</SheetTitle>
            <SheetDescription>
              {editingAdvanceId
                ? "Fix whatever needed changing, then resubmit - it goes straight back to whoever sent it back."
                : "Goes to your reporting manager for approval, then Finance for disbursement. Link it later to a City travel or Miscellaneous claim of the same type."}
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="-mx-6 flex-1">
            <div className="space-y-3 px-6 py-1">
              {advResubmitNote && (
                <div className="flex items-start gap-3 rounded-md border border-[hsl(var(--quadra-orange))] bg-[hsl(var(--quadra-orange)/0.1)] p-3 text-sm">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                  <div className="space-y-0.5">
                    <p className="font-medium text-amber-700 dark:text-amber-400">
                      Changes requested by {advResubmitNote.stage === "Finance" ? "Finance" : "your reporting manager"}
                    </p>
                    <p className="whitespace-pre-wrap text-foreground">{advResubmitNote.comment}</p>
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Designation</Label>
                <Input value={designation || "-"} disabled readOnly />
              </div>
              <div className="space-y-1.5">
                <Label>Claim type</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {OTHER_CLAIM_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAdvClaimType(t)}
                      // The claim type decides which claims it can link to -
                      // fixed once raised.
                      disabled={!!editingAdvanceId && t !== advClaimType}
                      className={`rounded-md border p-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        advClaimType === t ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <p className="font-medium">{OTHER_CLAIM_TYPE_LABEL[t]}</p>
                    </button>
                  ))}
                </div>
              </div>
              {!editingAdvanceId && (
                <div className="space-y-1.5">
                  <Label>Finance Approver</Label>
                  <Combobox
                    value={advFinanceApproverId}
                    onChange={setAdvFinanceApproverId}
                    options={financeApprovers.map((a) => ({
                      value: a.id,
                      label: a.displayName || a.email || a.id,
                      sublabel: a.email || undefined
                    }))}
                    placeholder={
                      financeApproversLoading
                        ? "Loading…"
                        : financeApprovers.length === 0
                          ? "None configured"
                          : "Select a Finance Approver"
                    }
                    searchPlaceholder="Search…"
                    disabled={financeApproversLoading || financeApprovers.length === 0}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="other-advance-purpose">Purpose</Label>
                <Textarea
                  id="other-advance-purpose"
                  value={advPurpose}
                  onChange={(e) => setAdvPurpose(e.target.value)}
                  placeholder="e.g. Local travel for client visits, October"
                  className="resize-none"
                  maxLength={500}
                />
                <p className="text-[0.6875rem] text-muted-foreground">
                  This is the advance's name - it's how you'll pick it when linking it to a claim.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Planned date</Label>
                <DatePicker value={advDate} onChange={setAdvDate} minDate={new Date()} />
                <p className="text-[0.6875rem] text-muted-foreground">
                  When you're going - for your record only, it isn't used for currency conversion.
                </p>
              </div>
              <OtherAdvanceAmountsField value={advAmounts} onChange={setAdvAmounts} idPrefix="other-advance" />
              {error && !reviewOpen && <p className="text-sm text-destructive">{error}</p>}
            </div>
          </ScrollArea>
          <SheetFooter className="mt-4">
            <SheetClose>
              <Button className="w-full" variant="outline">
                Cancel
              </Button>
            </SheetClose>
            <Button onClick={handleSubmitAdvanceClick} disabled={saving}>
              {editingAdvanceId ? "Resubmit" : "Submit request"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={reviewOpen}
        onOpenChange={(open) => {
          if (saving) return;
          setReviewOpen(open);
          if (!open) setError(null);
        }}
        title={editingAdvanceId ? "Resubmit this advance request?" : "Submit this advance request?"}
        description={
          editingAdvanceId
            ? "It goes straight back to whoever sent it back for changes."
            : hasReportingManager
              ? "It goes to your reporting manager for approval, then Finance for disbursement."
              : "You have no reporting manager on file, so it goes straight to your Finance Approver."
        }
        confirmLabel={editingAdvanceId ? "Confirm & resubmit" : "Confirm & submit"}
        pending={saving}
        onConfirm={confirmSubmitAdvance}
      >
        <div className="space-y-1 rounded-md border bg-muted/50 p-3 text-sm">
          {advClaimType && <SummaryRow label="Claim type" value={OTHER_CLAIM_TYPE_LABEL[advClaimType]} />}
          <SummaryRow label="Purpose" value={advPurpose.trim() || "-"} />
          <SummaryRow label="Planned date" value={advDate ? formatDate(advDate) : "-"} />
          {advAmounts.map((a) => (
            <SummaryRow
              key={a.currency}
              label={`Amount (${a.currency})`}
              value={`${a.currency} ${a.amount.toLocaleString()}`}
            />
          ))}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </ConfirmDialog>

      {financeNoticeOpen && (
        <FinanceNoticeDialog
          open
          onClose={() => {
            setFinanceNoticeOpen(false);
            setFinanceCc([]);
          }}
          toName={financeApprovers.find((a) => a.id === advFinanceApproverId)?.displayName ?? null}
          toEmail={financeApprovers.find((a) => a.id === advFinanceApproverId)?.email ?? null}
          cc={financeCc}
          onCcChange={setFinanceCc}
          onSend={() => void submitAdvance(financeCc)}
          sending={saving}
          error={error}
        />
      )}

      <ConfirmDialog
        open={!!discardTarget}
        onOpenChange={(open) => !open && setDiscardTarget(null)}
        title="Discard this draft?"
        description="This deletes the draft and any bills already uploaded to it. If you linked an advance, it stays available to link again later."
        confirmLabel="Discard"
        destructive
        pending={discarding}
        onConfirm={confirmDiscard}
      />

      <Dialog open={showDraftReminder} onOpenChange={setShowDraftReminder}>
        <DialogContent className={PHONE_SHEET_DIALOG}>
          <DialogHeader>
            <DialogTitle>You have unfinished draft {draftRows.length === 1 ? "claim" : "claims"}</DialogTitle>
            <DialogDescription>
              {draftRows.length === 1
                ? "You started a claim that was never submitted. Continue it, or start a fresh one instead."
                : `You started ${draftRows.length} claims that were never submitted. Continue the most recent one, or start a fresh one instead.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {draftRows.slice(0, 5).map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  setShowDraftReminder(false);
                  navigate(`/other-reimbursements/new?draftId=${encodeURIComponent(r.id)}`);
                }}
                className="w-full rounded-md border p-2.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <p className="font-medium">{r.flightDetails || claimTypeLabel(r.claimType)}</p>
                <p className="text-xs text-muted-foreground">
                  {claimTypeLabel(r.claimType)} · created {formatDate(r.createdAt)}
                </p>
              </button>
            ))}
            {draftRows.length > 5 && (
              <p className="text-xs text-muted-foreground">
                and {draftRows.length - 5} more - see the Draft filter below.
              </p>
            )}
          </div>
          <DialogFooter className={cn("sm:justify-between", PHONE_DIALOG_FOOTER)}>
            <Button
              variant="outline"
              onClick={() => {
                setShowDraftReminder(false);
                navigate("/other-reimbursements/new");
              }}
            >
              Start a new one anyway
            </Button>
            <Button
              onClick={() => {
                setShowDraftReminder(false);
                navigate(`/other-reimbursements/new?draftId=${encodeURIComponent(draftRows[0].id)}`);
              }}
            >
              Continue most recent draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RequestDetailDialog
        travelRequestId={viewRequestId}
        open={!!viewRequestId}
        onOpenChange={(open) => {
          if (!open) {
            setViewRequestId(null);
            setHighlightOnOpen(false);
          }
        }}
        highlight={highlightOnOpen}
      />
      <AdvanceDetailDialog
        advanceId={viewAdvanceId}
        open={!!viewAdvanceId}
        onOpenChange={(open) => {
          if (!open) {
            setViewAdvanceId(null);
            setHighlightOnOpen(false);
          }
        }}
        highlight={highlightOnOpen}
      />
    </PageContainer>
  );
}
